/**
 * Resolver FK / relación para "Seguir referencia" (self-join y cadena).
 * "Seguir" navega al catálogo de la tabla referenciada; "Añadir" materializa la columna.
 */

/** Máx. hops FK→FK (evita árboles infinitos / joins enormes). */
export const MAX_FOLLOW_DEPTH = 5;

function norm(s) {
  return String(s || '').toLowerCase();
}

/** Resuelve tableId aunque falte schema o cambie mayúsculas. */
export function findContextTable(context, tableId) {
  const tables = context?.tables || [];
  if (!tableId) return null;
  const exact = tables.find((t) => t.id === tableId);
  if (exact) return exact;
  const want = norm(tableId);
  const byId = tables.find((t) => norm(t.id) === want);
  if (byId) return byId;
  const short = want.includes('.') ? want.split('.').pop() : want;
  return (
    tables.find((t) => norm(t.id).split('.').pop() === short) ||
    tables.find((t) => norm(t.name) === short) ||
    null
  );
}

function sanitizeAliasPart(s) {
  return String(s || '')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^(\d)/, '_$1')
    .slice(0, 40);
}

/** Columna legible preferida en la tabla destino. */
export function preferDisplayColumn(table) {
  const cols = table?.columns || [];
  if (!cols.length) return null;
  const prefs = ['name', 'nombre', 'label', 'title', 'display_name', 'displayName'];
  for (const p of prefs) {
    const hit = cols.find((c) => norm(c.name) === p);
    if (hit) return hit.name;
  }
  const str = cols.find((c) => c.logicalType === 'string' && !c.isPrimaryKey);
  if (str) return str.name;
  return cols[0].name;
}

/** parent_asset_id → asset_id */
export function pkFromParentFkColumn(fromColumn, table) {
  const m = String(fromColumn || '').match(/^parent_(.+)$/i);
  if (!m) return null;
  const candidate = m[1];
  const hit = (table?.columns || []).find((c) => norm(c.name) === norm(candidate));
  return hit ? hit.name : null;
}

/** PK real de la tabla (vsaas_* suelen usar asset_id / sensor_id, no id). */
export function resolvePrimaryKeyColumn(table) {
  const cols = table?.columns || [];
  if (!cols.length) return 'id';
  const marked = cols.filter((c) => c.isPrimaryKey);
  if (marked.length === 1) return marked[0].name;
  const byId = cols.find((c) => norm(c.name) === 'id');
  if (byId) return byId.name;
  const tableName = norm(String(table.id || table.name || '').split('.').pop());
  const base = tableName.replace(/^vsaas_/, '');
  const singular = base.replace(/s$/, '');
  for (const name of [`${singular}_id`, `${base}_id`]) {
    const hit = cols.find((c) => norm(c.name) === name);
    if (hit) return hit.name;
  }
  if (marked.length) return marked[0].name;
  return cols[0].name;
}

/**
 * toColumn del follow. parent_*_id tiene prioridad sobre relaciones mal cargadas
 * (evita parent_asset_id → customer_id).
 */
export function resolveTargetPk(context, targetTableId, preferredColumn, fromColumn) {
  const dest = findContextTable(context, targetTableId);
  if (!dest) return preferredColumn || 'id';
  const fromParent = pkFromParentFkColumn(fromColumn, dest);
  if (fromParent) return fromParent;
  const pk = resolvePrimaryKeyColumn(dest);
  if (preferredColumn && dest.columns?.some((c) => c.name === preferredColumn)) {
    const pref = norm(preferredColumn);
    const tableName = norm(String(dest.id || '').split('.').pop()).replace(/^vsaas_/, '');
    const singular = tableName.replace(/s$/, '');
    if (pref === norm(pk) || pref === 'id' || pref === `${singular}_id` || pref === `${tableName}_id`) {
      return preferredColumn;
    }
  }
  return pk;
}

/**
 * Encuentra la columna del tipo parent_X_id de una tabla que se auto-referencia
 * (ej. vsaas_assets.parent_asset_id → vsaas_assets.asset_id). Usado para ofrecer
 * "preferir el nombre del padre" (coalesceParent) sin que el usuario tenga que armar
 * el join a mano con "Seguir referencia".
 */
export function findSelfParentFkColumn(context, tableId) {
  const table = findContextTable(context, tableId);
  if (!table) return null;
  for (const col of table.columns || []) {
    const target = resolveFollowTarget(context, tableId, col.name);
    if (target && norm(target.targetTableId) === norm(table.id)) {
      return { fromColumn: col.name, targetPk: target.targetPk };
    }
  }
  return null;
}

export function getFollowDepth(ref) {
  if (!ref) return 0;
  if (Array.isArray(ref.viaPath) && ref.viaPath.length) return ref.viaPath.length;
  if (ref.via?.depth != null) return Number(ref.via.depth) || 0;
  if (ref.joinAlias) return 1;
  return 0;
}

/**
 * @returns {{ targetTableId, targetPk, fromTableId, fromColumn, relationshipId?, label } | null}
 */
export function resolveFollowTarget(context, tableId, columnName) {
  if (!tableId || !columnName) return null;
  const relationships = context?.relationships || [];
  const table = findContextTable(context, tableId);
  const resolvedFromId = table?.id || tableId;
  const col = table?.columns?.find((c) => c.name === columnName);

  let rel = relationships.find(
    (r) => r.fromTableId === resolvedFromId && r.fromColumn === columnName
  );
  if (!rel) {
    rel = relationships.find(
      (r) =>
        norm(r.fromTableId) === norm(resolvedFromId) &&
        norm(r.fromColumn) === norm(columnName)
    );
  }
  // Jerarquía parent_X_id → X_id (antes que relaciones mal confirmadas)
  const parentPk = pkFromParentFkColumn(columnName, table);
  if (parentPk) {
    return {
      targetTableId: resolvedFromId,
      targetPk: parentPk,
      fromTableId: resolvedFromId,
      fromColumn: columnName,
      relationshipId: null,
      label: `${columnName} → ${resolvedFromId}.${parentPk} (self-join)`,
    };
  }

  if (rel) {
    const dest = findContextTable(context, rel.toTableId);
    const targetTableId = dest?.id || rel.toTableId;
    // Las relaciones manuales son una eleccion explicita del usuario (ej. unir por
    // una columna de negocio como "type", no la PK real de la tabla destino) --
    // resolveTargetPk existe para protegerse de relaciones auto-detectadas que
    // adivinaron mal, y esa heuristica de "se parece a una PK" rechaza justo el
    // caso manual que el usuario si quiso. Para 'manual' se respeta toColumn tal cual.
    const targetPk =
      rel.source === 'manual' && rel.toColumn
        ? rel.toColumn
        : resolveTargetPk(context, targetTableId, rel.toColumn, columnName);
    return {
      targetTableId,
      targetPk,
      fromTableId: findContextTable(context, rel.fromTableId)?.id || rel.fromTableId,
      fromColumn: rel.fromColumn,
      relationshipId: rel.id,
      label: rel.label || `${rel.fromColumn} → ${targetTableId}.${targetPk}`,
    };
  }

  if (col?.referencedTable) {
    const dest = findContextTable(context, col.referencedTable);
    const targetTableId = dest?.id || col.referencedTable;
    const targetPk = resolveTargetPk(
      context,
      targetTableId,
      col.referencedColumn,
      columnName
    );
    return {
      targetTableId,
      targetPk,
      fromTableId: resolvedFromId,
      fromColumn: columnName,
      relationshipId: null,
      label: `${columnName} → ${targetTableId}.${targetPk}`,
    };
  }

  const n = norm(columnName);
  if (n.includes('parent') && (n.endsWith('_id') || n.endsWith('id'))) {
    const pk = resolvePrimaryKeyColumn(table);
    return {
      targetTableId: resolvedFromId,
      targetPk: pk,
      fromTableId: resolvedFromId,
      fromColumn: columnName,
      relationshipId: null,
      label: `${columnName} → ${resolvedFromId}.${pk} (self-join)`,
    };
  }

  return null;
}

export function makeFollowJoinAlias(fromColumn, parentJoinAlias = null) {
  if (parentJoinAlias) {
    return `${sanitizeAliasPart(parentJoinAlias)}_${sanitizeAliasPart(fromColumn)}`.slice(0, 55);
  }
  return `via_${sanitizeAliasPart(fromColumn || 'ref')}`.slice(0, 55);
}

/** Alcance raíz: columnas de la tabla base (sin join). */
export function createRootBrowseScope(tableId) {
  if (!tableId) return null;
  return {
    tableId,
    joinAlias: null,
    via: null,
    depth: 0,
    viaPath: [],
    crumb: null,
  };
}

/**
 * Navega un hop por FK: muestra columnas de la tabla referenciada (no añade nada).
 * @returns {object|null} nuevo browse scope
 */
export function followBrowseScope(context, scope, fkColumn) {
  if (!scope?.tableId || !fkColumn) return null;
  if ((scope.depth || 0) >= MAX_FOLLOW_DEPTH) return null;

  const target = resolveFollowTarget(context, scope.tableId, fkColumn);
  if (!target) return null;

  const dest = findContextTable(context, target.targetTableId);
  if (!dest?.columns?.length) return null;
  const resolvedTargetId = dest.id;

  const nextDepth = (scope.depth || 0) + 1;
  const joinAlias = makeFollowJoinAlias(target.fromColumn, scope.joinAlias || null);
  const fromTableResolved =
    findContextTable(context, target.fromTableId)?.id || target.fromTableId;
  const via = {
    fromTableId: fromTableResolved,
    fromColumn: target.fromColumn,
    toColumn: target.targetPk,
    fromJoinAlias: scope.joinAlias || undefined,
    depth: nextDepth,
    relationshipId: target.relationshipId || undefined,
  };
  const step = {
    joinAlias,
    targetTableId: resolvedTargetId,
    fromTableId: via.fromTableId,
    fromColumn: via.fromColumn,
    toColumn: via.toColumn,
    fromJoinAlias: via.fromJoinAlias,
    depth: nextDepth,
    relationshipId: via.relationshipId,
  };

  return {
    tableId: resolvedTargetId,
    joinAlias,
    via,
    depth: nextDepth,
    crumb: fkColumn,
    viaPath: [...(scope.viaPath || []), step],
  };
}

/** Field ref listo para el dataset desde el alcance actual + columna elegida. */
export function buildFieldRefFromBrowse(scope, columnName) {
  if (!scope?.tableId || !columnName) return null;
  return {
    tableId: scope.tableId,
    column: columnName,
    role: 'row',
    aggregation: 'none',
    dateBucket: 'raw',
    jsonPath: '',
    joinAlias: scope.joinAlias || undefined,
    via: scope.via || undefined,
    viaPath: scope.viaPath?.length ? scope.viaPath : undefined,
    alias: undefined,
  };
}

/**
 * Partiendo de un field ref FK ya en el dataset, abre browse en la tabla referenciada.
 */
export function browseScopeFromFieldRef(context, fromRef) {
  if (!fromRef) return null;
  const base = {
    tableId: fromRef.tableId,
    joinAlias: fromRef.joinAlias || null,
    via: fromRef.via || null,
    depth: getFollowDepth(fromRef),
    viaPath: Array.isArray(fromRef.viaPath) ? [...fromRef.viaPath] : [],
    crumb: fromRef.via?.fromColumn || null,
  };
  // Si el field es la FK, el primer "Seguir" entra en el destino
  if (resolveFollowTarget(context, fromRef.tableId, fromRef.column)) {
    return followBrowseScope(context, base, fromRef.column);
  }
  return base;
}

/** @deprecated usar buildFieldRefFromBrowse + followBrowseScope */
export function buildFollowedColumnRef(context, fromRef, displayColumn) {
  const scope0 = {
    tableId: fromRef.tableId,
    joinAlias: fromRef.joinAlias || null,
    via: fromRef.via || null,
    depth: getFollowDepth(fromRef),
    viaPath: Array.isArray(fromRef.viaPath) ? [...fromRef.viaPath] : [],
  };
  const next = followBrowseScope(context, scope0, fromRef.column);
  if (!next) return null;
  return buildFieldRefFromBrowse(next, displayColumn);
}

export function canFollowFurther(context, ref) {
  if (!ref?.tableId || !ref?.column) return false;
  if (getFollowDepth(ref) >= MAX_FOLLOW_DEPTH) return false;
  return Boolean(resolveFollowTarget(context, ref.tableId, ref.column));
}
